/* eslint-disable max-lines */
/* eslint-disable import/no-duplicates */
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Buffer } from 'buffer';
import cloudUpload from '../../images/cloud-stmt-upload.svg';
import checkMark from '../../images/checkmark-circle.svg';
import gallery from '../../images/gallery.svg';
import deleteIcon from '../../images/delete-icon.svg';
import repeat from '../../images/repeat.png';
import UploadImageEN from '../../images/Upload_GIF_2.gif';
import UploadImageES from '../../images/Upload_GIF_2ES.gif';
import { SidebarStyles as classes } from './SidebarStyles';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import Loader from '../Loader/Loader';
import CustomTooltip from '../Tooltip/CustomTooltip';
import {
  ELITE_ACCOUNT_EN,
  FUND_UPLOAD_EXPIRED_CHECK,
  FUND_UPLOAD_VOID_CHECK,
} from '../../constants';
import { dgiMapping } from '../../services/dgiMapping';

import {
  generateFileMD5,
  uploadDocument,
  uploadDocumentByPreSignedURL,
  uploadDocumentXL,
} from '../../services/uploadService';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { businessMapping } from '../../services/businessMapping';
import { StatementUploadStyles as statementClasses } from './StatementUploadStyles';
import { productMapping } from '../../services/productMapping';
import { isCDIraproduct, isPlaidProduct } from '../commonCode';
import { CloudUpload, CloudUploadBtn } from '../../images/svg/CustomizedSVG';

const preSignedUrlResponse = (resp, documentId, leadGenerationContext) => {
  const {
    setStatementDocumentId,
    setStatementUploadLoading,
    setStatementUploadStatus,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setStatementError,
    setPlaidStatementMandatoryError,
  } = leadGenerationContext;
  if (resp && resp.status === 200) {
    setStatementDocumentId(documentId);
    setStatementUploadLoading(false);
    setStatementUploadStatus('DONE');
    setStatementError('');
    setPlaidStatementMandatoryError(false);
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    leadGenerationContext.setFundDataErrors((prevState) => ({
      ...prevState,
      statementDocumentId: '',
    }));
  } else {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
  }
};
const documentResponse = async (
  fileMD5,
  fileExtension,
  fileName,
  fileType,
  fileSize,
  file,
  leadGenerationContext
) => {
  const { setStatementUploadLoading, setStatementUploadStatus } = leadGenerationContext;
  const fileMd5Base64 = Buffer.from(fileMD5, 'hex').toString('base64');
  const response = await uploadDocumentXL(
    fileExtension,
    fileName,
    fileType,
    fileSize,
    fileMd5Base64,
    leadGenerationContext.accessToken,
    leadGenerationContext.setStatementUploadProgress,
    leadGenerationContext.setStatementLinearProgress
  );
  if (response?.data) {
    const documentId = response.data.documentId ? response.data.documentId : null;
    const preSignedUrl = response.data.preSignedUrl ? response.data.preSignedUrl : null;
    if (preSignedUrl && documentId) {
      const resp = await uploadDocumentByPreSignedURL(
        preSignedUrl,
        file,
        fileMd5Base64,
        leadGenerationContext.setStatementUploadProgress,
        leadGenerationContext.setStatementLinearProgress
      );
      preSignedUrlResponse(resp, documentId, leadGenerationContext);
    } else {
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
    }
  } else {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
  }
};

export const uploadFileStatement = async (file, leadGenerationContext, props) => {
  const {
    setStatementUploadStatus,
    setStatementDocumentId,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setStatementUploadLoading,
    setModalMessageGlobal,
    modalMessageGlobal,
    setPlaidStatementMandatoryError,
    setIsErrorGlobal,
    setStatementFileName,
    setStatementError,
    setStatementLinearProgress,
    setStatementFileSize,
  } = leadGenerationContext;
  setStatementUploadLoading(true);
  const fileName = file.name;
  const { fileType } = props;
  setStatementFileName(fileName);
  const fileParts = fileName.split('.');
  const fileExtension = fileParts[fileParts.length - 1].toLowerCase();
  const fileSize = file.size;
  setStatementFileSize(fileSize);

  if (fileSize > 10485760) {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
    setModalMessageGlobal({
      ...modalMessageGlobal,
      messageTitle: 'Document uploaded is not supported.',
      messageSubTitle: 'The file exceeds the maximum upload size (10 Mb).',
      typeName: 'statementUpload',
    });
    setStatementError('exceeded');
    // setOpenModalGlobal(true);
    setIsErrorGlobal(true);
    return;
  }
  const extensions = ['pdf', 'jpg', 'jpeg', 'png'];
  let isValidExtension = false;
  if (extensions.includes(fileExtension.toLowerCase())) isValidExtension = true;
  if (!isValidExtension) {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
    setStatementError('invalid format');
    setModalMessageGlobal({
      ...modalMessageGlobal,
      messageSubTitle: 'Only files with the following formats are allowed: PNG, JPG, PDF.',
      typeName: 'statementUpload',
    });
    // setOpenModalGlobal(true);
    setIsErrorGlobal(true);
    return;
  }
  const formData = new FormData();
  formData.append('file', file);
  if (fileSize < 10485760 && isValidExtension) {
    setStatementLinearProgress(true);
  }
  if (fileSize > 3145728) {
    const fileMD5 = await generateFileMD5(file);
    if (fileMD5) {
      await documentResponse(
        fileMD5,
        fileExtension,
        fileName,
        fileType,
        fileSize,
        file,
        leadGenerationContext
      );
    } else {
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
    }
  } else {
    const response = await uploadDocument(
      fileExtension,
      fileName,
      fileType,
      formData,
      leadGenerationContext.accessToken,
      leadGenerationContext.setStatementUploadProgress,
      leadGenerationContext.setStatementLinearProgress
    );
    if (response?.data) {
      setStatementDocumentId(response.data.documentId);
      setStatementUploadLoading(false);
      setStatementUploadStatus('DONE');
      setStatementError('');
      setPlaidStatementMandatoryError(false);
      setIsFundingScreenDirty(true);
      setIsEdeliveryScreenDirty(true);
      leadGenerationContext.setFundDataErrors((prevState) => ({
        ...prevState,
        statementDocumentId: '',
      }));
    } else {
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
    }
  }
};
function StatementUpload(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { pagePath, history, isBundle } = props;
  const {
    statementUploadStatus,
    productName,
    fundData,
    statementUploadLoading,
    setStatementUploadStatus,
    setStatementFileName,
    statementFileName,
    statementError,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setFundData,
    setStatementDocumentId,
    setFundDataErrors,
    setStatementFileSize,
    setStatementError,
    statementUploadProgress,
    setStatementLinearProgress,
    setBankUploadPreviewUrl,
    theme,
    plaidDetails,
    language,
  } = leadGenerationContext;
  const isVerificationPending =
    plaidDetails?.remarks === 'pending_manual_verification' && plaidDetails?.status === 'Success';
  const isPlaidSuccess = plaidDetails?.status === 'Success' && !plaidDetails?.remarks;

  const fileInput = useRef(null);

  const uploadStatementImage =
    leadGenerationContext.language === 'EN' ? UploadImageEN : UploadImageES;

  const [flowCheck, setFlowCheck] = useState('');
  useEffect(() => {
    if (businessMapping(productName)) {
      setFlowCheck('commercial-form');
    } else {
      setFlowCheck('retail-form');
    }
  }, []);
  const checkFileInput = () => {
    if (fileInput?.current?.value) {
      fileInput.current.value = '';
    }
  };

  const buttonClickHandler = (e) => {
    e.preventDefault();
    checkFileInput();
    fileInput.current?.click();
  };
  const removeUploadedFile = () => {
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    setStatementUploadStatus(null);
    setStatementFileName('');
    setStatementFileSize('');
    setStatementError('');
    setStatementDocumentId(null);
    setBankUploadPreviewUrl('');

    if (
      productMapping(leadGenerationContext.productName) === ELITE_ACCOUNT_EN ||
      productMapping(leadGenerationContext.productName) === 'Ahorra' ||
      isCDIraproduct(leadGenerationContext.productName)
    ) {
      setFundData((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
      setFundDataErrors((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
    }
  };

  useEffect(() => {
    if (statementUploadProgress === 100) {
      const timeout = setTimeout(() => {
        setStatementLinearProgress(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [statementUploadProgress]);

  let status;
  const uploadStatusNotDone = () => {
    if (statementUploadStatus !== 'DONE') {
      status = '0';
    } else {
      status = '-1';
    }
    return status;
  };

  let uploadStatement;
  const uploadStatementGif = () => {
    if (statementUploadStatus !== 'DONE' && !statementUploadLoading) {
      uploadStatement = (
        <Box component="div" sx={classes.new_addr_gif}>
          <img src={uploadStatementImage} alt={t('Upload statement funding screen')} />
        </Box>
      );
      // } else {
      //   uploadStatement = (
      //     <div className={classes.new_addr_gif}>
      //       <img
      //         tabIndex="0"
      //         className={classes.new_addr_gif_width_adjust}
      //         src={uploadStatementImage}
      //         alt={t('Upload document funding screen')}
      //       />
      //     </div>
      //   );
      // }
    }
    return uploadStatement;
  };
  const fileInputHandler = async (e) => {
    e.preventDefault();
    const file = e?.target?.files?.[0];
    if (file !== undefined && file !== null) {
      setBankUploadPreviewUrl(URL.createObjectURL(file));
      uploadFileStatement(file, leadGenerationContext, props);
    }
  };

  // useImperativeHandle(ref, () => ({
  //   triggerUpload: (e) => {
  //     fileInputHandler(e, leadGenerationContext, props);
  //   },
  // }));

  // const uploadBankStatementAltVal = (language, uploadStatus) => {
  //   let val;
  //   if (uploadStatus === null) {
  //     if (language === 'EN') val = 'Upload a Void check or Bank Statement Required';
  //     else val = 'Suba un cheque cancelado o estado bancario requerido';
  //   } else if (uploadStatus === 'DONE') {
  //     if (language === 'EN') val = 'Upload success';
  //     else val = 'Subir Listo';
  //   } else val = '';
  //   return val;
  // };
  // useEffect(() => {
  //   let element = '';
  //   element = document.getElementById('statementUploadContainer');
  //   if (statementUploadStatus === 'DONE' && !statementUploadLoading) {
  //     if (element) {
  //       element.setAttribute(
  //         'style',
  //         'background-image: repeating-linear-gradient(0deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#E37617,#E37617 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#E37617,#E37617 7px,transparent 8px,transparent 15px) !important; background:#FFFDF2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
  //       );
  //     }
  //   } else if (statementUploadStatus === 'FAILED' && !statementUploadLoading) {
  //     if (element) {
  //       element.setAttribute(
  //         'style',
  //         'background-image: repeating-linear-gradient(0deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#D71721,#D71721 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#D71721,#D71721 7px,transparent 8px,transparent 15px) !important; background:#FFF2F2;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
  //       );
  //     }
  //   } else {
  //     element.setAttribute(
  //       'style',
  //       'background-image: repeating-linear-gradient(0deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(90deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(180deg,#808285,#808285 7px,transparent 8px,transparent 15px),repeating-linear-gradient(270deg,#808285,#808285 7px,transparent 8px,transparent 15px), !important;background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;background-repeat: no-repeat;background-position: 0 0, 0 0, 100% 0, 0 100%'
  //     );
  //   }
  // }, [statementUploadStatus]);
  const routeToCameraUpload = () => {
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    history.push({
      pathname: `/${leadGenerationContext.location?.toLowerCase()}/${leadGenerationContext.language?.toLowerCase()}/${flowCheck}/${productNameURLMapping(
        productName
      )}/camera-upload`,
      redirectingPath: pagePath,
      pageProps: props,
      flow: flowCheck,
    });
  };

  return (
    <Grid container style={{ height: '100%', width: '100%' }}>
      <Grid
        sx={{
          ...(isBundle ? statementClasses.mainContainerBundle : statementClasses.mainContainer),
          backgroundColor: isBundle ? theme?.palette?.plaidCardBg?.main : '#F8F8F8',
          border: isBundle
            ? `1px solid ${theme?.palette?.plaidCardBorder?.main}`
            : '1px solid #D0D0D0',
          borderRadius: isBundle ? '24px' : '4px',
        }}
        aria-label={t('Upload Bank Statement', {
          defaultValue: 'Upload Bank Statement',
          nsSeparator: '|',
        })}
      >
        {/* <img
          src={cloudUpload}
          height="42px"
          width="42px"
          alt={uploadBankStatementAltVal(leadGenerationContext.language, statementUploadStatus)}
        /> */}
        <div style={{ width: '45px', height: '45px' }}>
          <CloudUploadBtn
            height="100%"
            width="100%"
            color={theme?.palette?.orientalPrimary?.main}
          />
        </div>

        <Typography
          // component="h5"
          data-testid="statement-proof"
          sx={
            isBundle
              ? {
                  ...statementClasses.Bundletext1,
                  fontSize: language === 'en' ? '20px !important' : '18px !important',
                  color: theme?.palette?.primary?.main,
                  alignItems: 'center',
                }
              : statementClasses.text1
          }
          aria-label={t('Upload Bank Statement', {
            defaultValue: 'Upload Bank Statement',
            nsSeparator: '|',
          })}
        >
          {t('Upload Bank Statement', {
            defaultValue: 'Upload Bank Statement',
            nsSeparator: '|',
          })}
          {/* <span
            style={{
              fontSize: '22px',
              color: ORIENTAL_ORANGE,
              position: 'absolute',
              right: '23px',
              top: '-8px',
            }}
          >
            *
          </span> */}
          <span style={{ marginLeft: isBundle ? '5px' : '15px', opacity: 1, cursor: 'pointer' }}>
            {dgiMapping(productName) ? (
              <CustomTooltip
                placement="top"
                text={t(`PreviewStatementDgi`, {
                  defaultValue: `Sensitive Information will be encrypted and safeguarded in our systems. A blank Void check or bank statement from the last 60 days is required to confirm your account. The numbering for the void check must be equal to or greater than 100. If the account is not currently under your name, additional information will be requested.`,
                })}
              />
            ) : (
              <CustomTooltip
                placement="top"
                text={t(`PreviewStatement`, {
                  defaultValue: `Sensitive Information will be encrypted and safeguard in our systems. A blank Void check or bank statement from the last 60 days is required to confirm your account.`,
                })}
              />
            )}
          </span>
        </Typography>
        {statementUploadStatus !== 'DONE' && (
          <input
            type="file"
            data-testid="inp_test"
            accept=".pdf,.jpg,.jpeg,.jfif,.bmp,.bpm,.pgm,.ppm,.png,.gif,.gifv,.tiff,.heic,.heif,.hevc,.mpg,.mpeg"
            onChange={(e) => fileInputHandler(e, leadGenerationContext, props)}
            ref={fileInput}
            style={{ display: 'none' }}
          />
        )}
        {statementUploadLoading && (
          <Box component="div" sx={classes.errorImgPosition}>
            <Loader />
          </Box>
        )}
        {statementUploadStatus === null && !statementUploadLoading && (
          <>
            <Typography
              color="subtext"
              variant={isBundle ? 'body1' : 'subtitle1'}
              component="p"
              id="uploadVoidCheck"
              sx={{
                ...(isBundle ? statementClasses.Bundletext2 : statementClasses.text2),
                ...(isBundle
                  ? language === 'EN'
                    ? {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        margin: '0px !important',
                      }
                    : {
                        marginBottom: '20px',
                      }
                  : {
                      marginBottom: '25px',
                    }),
              }}
            >
              {isBundle
                ? t(FUND_UPLOAD_EXPIRED_CHECK, {
                    defaultValue: FUND_UPLOAD_EXPIRED_CHECK,
                    nsSeparator: '|',
                  })
                : t(FUND_UPLOAD_VOID_CHECK, {
                    defaultValue: FUND_UPLOAD_VOID_CHECK,
                    nsSeparator: '|',
                  })}
            </Typography>

            <Box component="div" sx={statementClasses.btnContainer}>
              <div>
                <Button
                  disableFocusRipple
                  id="uploadDocId"
                  variant="outlined"
                  color="primary"
                  data-testid="null_testid"
                  aria-describedby="uploadVoidCheck"
                  onClick={(e) => buttonClickHandler(e)}
                  tabIndex={uploadStatusNotDone()}
                  disabled={isPlaidSuccess || isVerificationPending}
                  classes={{ disabled: statementClasses.disabledButton }}
                  sx={
                    isBundle
                      ? {
                          ...statementClasses.uploadBtnBundle,
                          ...(language === 'ES' ? { marginTop: '0px' } : { marginTop: '32px' }),
                          color: theme?.palette?.orientalPrimary?.main,
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          ':focus': {
                            outline: `2px solid ${theme?.palette?.orientalPrimary?.main}`,
                            outlineOffset: '2px',
                          },
                          '&.MuiButton-root.Mui-disabled': {
                            background: theme?.palette?.disabledButtonBackground?.main,
                            borderRadius: '56px',
                            color: theme?.palette?.disabledButton?.main,
                            border: 'none',
                          },
                        }
                      : statementClasses.uploadBtn
                  }
                >
                  {t('Upload Now', {
                    defaultValue: 'Upload Now',
                  })}
                </Button>
              </div>
              {!isBundle && (
                <div>
                  <Button
                    disableFocusRipple
                    color="inherit"
                    variant="outlined"
                    onClick={routeToCameraUpload}
                    disabled={isPlaidSuccess || isVerificationPending}
                    data-testid="camera_testid"
                    sx={statementClasses.cameraBtn}
                  >
                    {t('Camera', {
                      defaultValue: 'Camera',
                    })}
                  </Button>
                </div>
              )}
            </Box>
            {!isBundle && (
              <Typography variant="body1" sx={statementClasses.text3}>
                {t('Supported file types: PNG, JPG, PDF. Maximum: 10MB', {
                  defaultValue: 'Supported file types: PNG, JPG, PDF. Maximum: 10MB',
                  nsSeparator: '|',
                })}
              </Typography>
            )}
          </>
        )}
        {statementUploadStatus === 'DONE' &&
          !statementUploadLoading &&
          (isBundle ? (
            <>
              <Typography sx={statementClasses.Bundletext2} id="uploadVoidCheck">
                {t(FUND_UPLOAD_EXPIRED_CHECK, {
                  defaultValue: FUND_UPLOAD_EXPIRED_CHECK,
                  nsSeparator: '|',
                })}
              </Typography>

              <Box component="div" sx={statementClasses.btnContainer}>
                <div>
                  <Button
                    disableFocusRipple
                    color="inherit"
                    id="uploadDocId"
                    variant="outlined"
                    aria-describedby="uploadVoidCheck"
                    onClick={(e) => buttonClickHandler(e)}
                    tabIndex={uploadStatusNotDone()}
                    disabled={
                      isPlaidSuccess || isVerificationPending || statementUploadStatus === 'DONE'
                    }
                    sx={{
                      ...(isBundle ? statementClasses.uploadBtnBundle : statementClasses.uploadBtn),
                      ...(language === 'ES' ? { marginTop: '18px' } : { marginTop: '32px' }),
                      '&.Mui-disabled': {
                        backgroundColor: theme?.palette?.disabledButtonBackground?.main,
                        borderRadius: '56px',
                        color: theme?.palette?.disabledButton?.main,
                        border: 'none',
                        pointerEvents: 'none',
                        cursor: 'default',
                        ':hover': {
                          backgroundColor: theme?.palette?.disabledButtonBackground?.main,
                        },
                        ':focus': {
                          outline: 'none',
                        },
                      },
                    }}
                    data-testid="upload-now-id"
                  >
                    {t('Upload Now', {
                      defaultValue: 'Upload Now',
                    })}
                  </Button>
                </div>
              </Box>
            </>
          ) : (
            <Box component="div" sx={statementClasses.uploadDone}>
              <img src={gallery} height="24px" width="24px" alt="success icon" />
              <div>
                <Typography sx={statementClasses.fileName}>{statementFileName}</Typography>
                <Typography sx={statementClasses.uploadSuccessMssg}>
                  {t('Uploaded successfully', {
                    defaultValue: 'Uploaded successfully',
                    nsSeparator: '|',
                  })}
                </Typography>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <img
                  src={checkMark}
                  height="18px"
                  width="18px"
                  alt="success check mark"
                  style={{ cursor: 'pointer' }}
                />
                <img
                  src={deleteIcon}
                  height="18px"
                  width="18px"
                  alt="delete icon"
                  style={{ cursor: 'pointer' }}
                  onClick={removeUploadedFile}
                  onKeyDown={removeUploadedFile}
                />
              </div>
            </Box>
          ))}
        {statementUploadStatus === 'FAILED' && !statementUploadLoading && !isBundle && (
          <Box component="div" sx={statementClasses.uploadFail}>
            <img src={gallery} height="24px" width="24px" alt="success icon" />
            <div>
              <Typography sx={statementClasses.fileName}>{statementFileName}</Typography>

              <Typography sx={statementClasses.uploadFailMssg}>
                {statementError === 'exceeded'
                  ? t('Upload failed.File size exceeded', {
                      defaultValue: 'Upload failed.File size exceeded',
                      nsSeparator: '|',
                    })
                  : t('Upload failed. File format not supported', {
                      defaultValue: 'Upload failed. File format not supported',
                      nsSeparator: '|',
                    })}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img
                src={repeat}
                height="20px"
                width="20px"
                style={{ cursor: 'pointer' }}
                id="uploadAddressProofFail"
                data-testid="failed_id"
                onClick={(e) => buttonClickHandler(e)}
                onKeyDown={(e) => buttonClickHandler(e)}
                alt="Upload Failed"
              />
              <img
                src={deleteIcon}
                height="18px"
                width="18px"
                alt="delete icon"
                style={{ cursor: 'pointer' }}
                onClick={removeUploadedFile}
                onKeyDown={removeUploadedFile}
              />
            </div>
          </Box>
        )}
      </Grid>

      {!isPlaidProduct(productName) && (
        <Grid size={{ xs: 12 }} sx={classes.id_upload_grid}>
          {fundData?.statementUpload ? uploadStatementGif() : null}
        </Grid>
      )}
    </Grid>
  );
}
export default withRouter(StatementUpload);
StatementUpload.propTypes = {
  // fileType: PropTypes.string,
  pagePath: PropTypes.string,
  isBundle: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
